/**
 * Money is an integer count of the currency's minor units plus an ISO 4217
 * code (ARCHITECTURE.md §5). Conversion to and from display amounts goes
 * through the currency's own exponent, never an assumed 100.
 */
export interface Money {
  minor: number;
  currency: string;
}

const exponents = new Map<string, number>();

/** Minor-unit exponent: 2 for USD and EUR, 0 for JPY, 3 for KWD. */
export function currencyExponent(currency: string): number {
  let e = exponents.get(currency);
  if (e === undefined) {
    e = new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
    exponents.set(currency, e);
  }
  return e;
}

/** 12.5 USD → 1250. Rounds half away from zero at the currency's precision. */
export function toMinor(amount: number, currency: string): number {
  const f = 10 ** currencyExponent(currency);
  return Math.sign(amount) * Math.round(Math.abs(amount) * f);
}

/** 1250 USD → 12.5 */
export function fromMinor(minor: number, currency: string): number {
  return minor / 10 ** currencyExponent(currency);
}

/** Parses what a person typed ("12.50", "$12.5", "1,200") into minor units, or null. */
export function parseAmount(input: string, currency: string): number | null {
  const cleaned = input.replace(/[^\d.,-]/g, "").replace(/,(?=\d{3}\b)/g, "").replace(",", ".");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? toMinor(n, currency) : null;
}

export function formatMoney(minor: number | null | undefined, currency: string, locale = "en-US"): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return "—";
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(fromMinor(minor, currency));
}

/** A percentage of an amount, rounded to a whole minor unit. */
export function pctOf(minor: number, pct: number): number {
  return Math.round((minor * pct) / 100);
}
